import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchMe, fetchSubscriptions, logoutRequest } from "../api";
import { getSessionToken, clearSessionToken } from "../utils/auth";
import { buildRosteredKeys } from "../utils/rosteredNames";
import SignInForm from "./SignInForm";
import SubscriptionList from "./SubscriptionList";

// Full-page manager for "notify me when available" subscriptions, at /notifications.
// This is the canonical place to review and cancel notifications; unlike the roster
// bells it lists everyone you follow regardless of whether they're working today.
// rosterData is passed down from App so this page doesn't refetch the roster.
function NotificationsPage({ rosterData }) {
  const [me, setMe] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [signedIn, setSignedIn] = useState(Boolean(getSessionToken()));

  const rosteredKeys = useMemo(() => buildRosteredKeys(rosterData), [rosterData]);

  const refresh = useCallback(async () => {
    if (!getSessionToken()) {
      setMe(null);
      setSubs([]);
      setSignedIn(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const [meData, subsData] = await Promise.all([fetchMe(), fetchSubscriptions()]);
      setMe(meData);
      setSubs(subsData);
      setSignedIn(true);
    } catch {
      // A rejected session token means the session expired - drop it and show sign-in
      // rather than leaving the page stuck in a broken signed-in state.
      clearSessionToken();
      setMe(null);
      setSubs([]);
      setSignedIn(false);
      setLoadError("Your session expired. Please sign in again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    window.addEventListener("auth-changed", refresh);
    window.addEventListener("subscriptions-changed", refresh);
    return () => {
      window.removeEventListener("auth-changed", refresh);
      window.removeEventListener("subscriptions-changed", refresh);
    };
  }, [refresh]);

  useEffect(() => {
    document.title = "My Notifications | Sydney Roster Scheduler";

    // A personal account page has no business in search results.
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    return () => robots.remove();
  }, []);

  const handleLogout = async () => {
    try {
      await logoutRequest();
    } catch {
      // Clearing the local token still signs the user out client-side.
    }
    clearSessionToken();
    setMe(null);
    setSubs([]);
    setSignedIn(false);
  };

  return (
    <section className="notifications-page" style={{ maxWidth: 680, margin: "0 auto" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h4 mb-0">🔔 My Notifications</h1>
        <Link to="/" className="btn btn-sm btn-outline-secondary">
          ← Back to rosters
        </Link>
      </div>

      {loadError && (
        <div className="alert alert-warning" role="alert">
          {loadError}
        </div>
      )}

      {!signedIn ? (
        <div className="card shadow-sm">
          <div className="card-body">
            <h2 className="h6">Sign in to manage notifications</h2>
            <SignInForm blurb="Sign in with your email to see and change who you're following. We'll email you when someone you follow appears on tomorrow's roster." />
          </div>
        </div>
      ) : loading ? (
        <div className="card shadow-sm">
          <div className="card-body text-muted">Loading your notifications...</div>
        </div>
      ) : (
        <>
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <h2 className="h6 text-muted text-uppercase small mb-2">Account</h2>
              <p className="mb-2">
                Signed in as <strong>{me?.email}</strong>
              </p>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleLogout}
              >
                Sign out
              </button>
            </div>
          </div>

          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <h2 className="h6 text-muted text-uppercase small mb-2">Telegram alerts</h2>
              {me?.telegram_linked ? (
                <span className="text-success small">
                  ✅ Linked — you'll get a Telegram message as well as an email.
                </span>
              ) : (
                <div className="small text-muted">
                  Want instant alerts? Message{" "}
                  {me?.telegram_bot_username ? (
                    <a
                      href={`https://t.me/${me.telegram_bot_username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      @{me.telegram_bot_username}
                    </a>
                  ) : (
                    "our Telegram bot"
                  )}{" "}
                  with this code:
                  <br />
                  <code>/start {me?.telegram_link_code}</code>
                </div>
              )}
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h2 className="h6 text-muted text-uppercase small mb-0">
                  Following ({subs.length})
                </h2>
              </div>
              <p className="text-muted small">
                Remove anyone here to stop their notifications — this works even if they
                aren't on the roster right now. Names marked{" "}
                <span className="badge bg-success" style={{ fontSize: "0.7em" }}>
                  on roster
                </span>{" "}
                are working today or tomorrow, and open their profile when clicked.
              </p>
              <SubscriptionList
                subscriptions={subs}
                onChanged={refresh}
                rosteredKeys={rosteredKeys}
              />
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default NotificationsPage;
